/**
 * SheetCrewPlannerPage - Spreadsheet-style crew planner
 * 
 * EF-SHEET-PLANNER: UI-Isolated Addition
 * EF-SHEET-PLANNER-REFINE v1.2: Navigation Alignment + Column Resize
 * EF-PLANNER-SHARED-CREW-ACTIONS v1.0: Uses shared copy/paste module
 * EF-PLANNER-DEPRECATION-AND-RENAMING-V1: Renamed to "Crew Planner" (ACTIVE)
 * 
 * Navigation Contract:
 * - Clickable entities use same patterns as SHIFT Crew Planner
 * - Project/Shift/Venue/Location/Crew names navigate to their pages
 * - Return URL via state for proper back navigation
 * 
 * Explicit Save Model:
 * - All edits remain in local state until Save button clicked
 * - updateShiftWithRules() called ONLY on explicit save
 * - OOT is display-only (never recalculated)
 */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DndContext, DragOverlay, useSensor, useSensors, PointerSensor, useDraggable } from "@dnd-kit/core";
import {
  getShifts,
  getProjects,
  getVenues,
  getVenueLocations,
  getShiftCrew,
  getShiftLineItems,
  addShiftCrew,
  updateShiftCrew,
  removeShiftCrew,
  updateShiftWithRules,
  getStaffList,
  pushShiftTaskToCrew,
  sendStaffPWALink,
  extractErrorMessage,
} from "../lib/api";
import api from "../lib/api";
import { filterShiftsByDisplayRange } from "../lib/crewPlannerDateFilter";
import { handleCopyCrew, handlePasteCrew, commitPasteOverride } from "../components/planner/sharedCrewActions";
// EVENTSFLO-CREW-PLANNER-EMAIL-CREW-RESTORE-V1
import PlannerEmailCrewModal from "../components/planner/PlannerEmailCrewModal";
// EVENTSFLO-CREW-PWA-MESSAGE-CENTRE-V1
import AdminMessagesBell from "../components/admin/AdminMessagesBell";
// EVENTSFLO-UI-HELP-BUTTON-CREW-PLANNER-V1
import PageHelpButton from "../components/common/PageHelpButton";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../components/ui/hover-card";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "../components/ui/context-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Users,
  Search,
  RefreshCw,
  Loader2,
  ZoomIn,
  ZoomOut,
  GripVertical,
  User,
  TableProperties,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  GripHorizontal,
  Clock,
  MapPin,
  Pencil,
  AlertTriangle,
  Eye,
  EyeOff,
} from "lucide-react";
import { cn } from "../lib/utils";
import { SheetPlannerRow } from "../components/planner/SheetPlannerRow";
import { CrewReadinessChip } from "../components/planner/CrewReadinessChip";

// ============================================================
// ZOOM LEVELS - Proportional scaling (font, row height)
// ============================================================
const ZOOM_LEVELS = [
  { key: "xs", label: "70%", scale: 0.7 },
  { key: "sm", label: "85%", scale: 0.85 },
  { key: "md", label: "100%", scale: 1.0 },
  { key: "lg", label: "115%", scale: 1.15 },
  { key: "xl", label: "130%", scale: 1.3 },
];

const ZOOM_STORAGE_KEY = "sheet-planner-zoom";
const PANEL_WIDTH_KEY = "sheet-planner-panel-width";

// EF-PLANNER-DEPRECATION-AND-RENAMING-V1: Visibility toggle storage keys
const HIDE_COMPLETED_SHIFTS_KEY = "hideCompletedShiftsPlanner";
const HIDE_ARCHIVED_PROJECTS_KEY = "hideArchivedProjectsPlanner";

// EF-PLANNER-DEPRECATION-AND-RENAMING-V1: Visibility toggle helpers
function getStoredHideCompletedShifts() {
  try {
    const stored = localStorage.getItem(HIDE_COMPLETED_SHIFTS_KEY);
    // Default to true (hide completed shifts by default for cleaner view)
    if (stored === null) return true;
    return stored === "true";
  } catch (e) { return true; }
}

function setStoredHideCompletedShifts(value) {
  try {
    localStorage.setItem(HIDE_COMPLETED_SHIFTS_KEY, String(value));
  } catch (e) { /* ignore */ }
}

function getStoredHideArchivedProjects() {
  try {
    const stored = localStorage.getItem(HIDE_ARCHIVED_PROJECTS_KEY);
    // Default to true (hide archived projects by default)
    if (stored === null) return true;
    return stored === "true";
  } catch (e) { return true; }
}

function setStoredHideArchivedProjects(value) {
  try {
    localStorage.setItem(HIDE_ARCHIVED_PROJECTS_KEY, String(value));
  } catch (e) { /* ignore */ }
}

// EVENTSFLO-CREW-PLANNER-DATE-FILTER-SORT-AND-COLUMN-WIDTH-V1:
// Base default column widths. These are the MIN widths — the grid will
// proportionally grow the "growable" text columns to fill the container.
// Numbers bumped so key project/venue/location/address columns are no
// longer cramped on typical laptop screens.
const DEFAULT_COLUMN_WIDTHS = {
  project: 160,
  shift: 180,
  venue: 170,
  location: 170,
  address: 260,
  date: 78,
  start: 60,
  end: 60,
  hours: 55,
  crew: 65,
};

// EVENTSFLO-CREW-PLANNER-DATE-FILTER-SORT-AND-COLUMN-WIDTH-V1:
// Columns that grow to fill available container width. Narrow numeric/time
// columns stay compact so key text columns get the extra space.
const GROWABLE_COLUMNS = ["project", "shift", "venue", "location", "address"];

// EVENTSFLO-CREW-PLANNER-DATE-FILTER-SORT-AND-COLUMN-WIDTH-V1:
// Date-range display filter options for the Crew Planner top control row.
const DISPLAY_RANGE_OPTIONS = [
  { value: "all", label: "All" },
  { value: "today", label: "Today" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
];
const DISPLAY_RANGE_KEY = "crewPlannerDisplayRange";

// Crew sort options (matching SHIFT Crew Planner)
const SORT_OPTIONS = [
  { value: "preference", label: "MET ↓" },
  { value: "name_asc", label: "A–Z" },
  { value: "name_desc", label: "Z–A" },
];

// EVENTSFLO-UI-HELP-BUTTON-CREW-PLANNER-V1
// Help content for the Crew Planner page. Kept co-located with the page
// it documents to minimise drift; the rules wording mirrors the
// deterministic thresholds in `backend/services.py` (Crew Chief at
// `crew_chief_threshold_1` ≥ 4, Meal at `meal_break_threshold_hours` > 5).
const CREW_PLANNER_HELP_SECTIONS = [
  {
    heading: "Page purpose",
    bullets: [
      "Spreadsheet-style planner for assigning available crew to project shifts.",
      "One row per shift; expand a row to manage assignments and shift details inline.",
      "Drag a crew member from the left Crew Pool onto a shift to assign them, or use the per-shift assigned-crew list.",
    ],
  },
  {
    heading: "Crew Pool (left-side picker)",
    bullets: [
      'Crew are displayed as "SURNAME, First name" to match operational sort order.',
      'Sort modes (top-right of the pool): "MET ↓" (preference score, highest first), "A–Z" and "Z–A" — A–Z/Z–A sort by surname then first name.',
      "Search filters by either first name or surname. Only active crew appear in the pool.",
      "Right-click a crew item to open their profile (Edit Crew).",
    ],
  },
  {
    heading: "Assigned crew list & scroll behaviour",
    bullets: [
      "Expand a shift row to view its assigned crew; multiple long lists scroll independently inside the expanded panel.",
      "Up to two shift rows can be expanded at once — opening a third closes the oldest.",
      "Each assigned crew row exposes a context menu: set Crew Chief, mark Confirmed/Worked, Push to App, Email Crew, Send PWA link, Copy/Paste crew set.",
    ],
  },
  {
    heading: "Crew Chief requirement (≥ 4 crew)",
    bullets: [
      "When a shift's effective crew count is 4 or more, a Crew Chief is required.",
      "The rule is deterministic — the threshold lives in `rule_variables.crew_chief_threshold_1` (default 4).",
      "Mark exactly one assigned crew member as Crew Chief via their context menu; the assignment role becomes \"<role> (Crew Chief)\".",
    ],
  },
  {
    heading: "Meal allowance rule (> 5 hours)",
    bullets: [
      "When a shift's duration is over 5 hours, a Meal allowance line item is required per crew member.",
      "The rule is deterministic — the threshold lives in `rule_variables.meal_break_threshold_hours` (default 5).",
      "The rules engine auto-suggests the meal line item on save; do not duplicate it manually.",
    ],
  },
  {
    heading: "OOT / travel tasks",
    bullets: [
      "Out-of-town (OOT) and travel-time task line items are visible on the expanded row when they have been computed.",
      "OOT is display-only in the planner — it is computed elsewhere and never recalculated by the planner save.",
      "If a travel/OOT value looks wrong, fix it on the shift or task definition, not in the planner.",
    ],
  },
  {
    heading: "Overstaffing / understaffing indicators",
    bullets: [
      "The Crew column shows `assigned / required` for each shift.",
      "When assigned < required → understaffed (visual emphasis).",
      "When assigned > required → overstaffed (visual emphasis).",
    ],
  },
  {
    heading: "Preference score (MET)",
    bullets: [
      "Each crew pool item shows a 2-digit MET score (left of the avatar) summarising how well the crew matches required skills / availability.",
      "Sorting by \"MET ↓\" lists the highest-matching crew first.",
      "MET is informational — assignment is still controlled by the operator.",
    ],
  },
  {
    heading: "Save / reopen persistence (Explicit Save Model)",
    bullets: [
      "Inline edits to a shift (start/end, location, staff required, notes) remain LOCAL until you click Save on that row.",
      "Crew assignment changes (add/remove/Crew Chief/confirmed/worked) persist IMMEDIATELY — there is no Save for those.",
      "Cancel discards local shift edits but does not undo persisted crew changes.",
      "Reopening the page re-fetches shifts and assignments — unsaved local edits are lost.",
    ],
  },
  {
    heading: "Conflict & availability warnings",
    bullets: [
      "Assigning a crew member who is already booked on an overlapping shift triggers a conflict dialog — provide a reason to override.",
      "Assigning a crew member who is not active / missing required onboarding documents triggers an onboarding override dialog where eligible.",
      "Pasting a copied crew set surfaces a single batch override dialog listing each blocked crew member.",
    ],
  },
  {
    heading: "Test data rule",
    bullets: [
      'Any test crew, test shifts and test projects MUST include the word "TEST" in their name.',
      "This keeps automated regression data segregated from real operational data and audit trails.",
    ],
  },
];

const CREW_PLANNER_HELP_WARNINGS = [
  "Crew assignment changes persist immediately — Cancel does NOT undo them.",
  "OOT / travel values are read-only here; fix them on the shift or task definition, not the planner.",
  "Inline shift edits (start/end/location/staff_required/notes) require an explicit Save click — closing the row or refreshing discards them.",
  "Crew Chief threshold (≥ 4) and Meal threshold (> 5 h) are deterministic — the planner will not skip them.",
  "Conflict and onboarding override actions are audited — only override with a clear, recorded reason.",
];

// ============================================================
// RESIZABLE COLUMN HEADER
// ============================================================
function ResizableColumnHeader({ children, width, onResize, minWidth = 40, className }) {
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    isDragging.current = true;
    startX.current = e.clientX;
    startWidth.current = width;
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    const delta = e.clientX - startX.current;
    const newWidth = Math.max(minWidth, startWidth.current + delta);
    onResize(newWidth);
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  };

  return (
    <div 
      className={cn("relative flex items-center", className)}
      style={{ width: `${width}px`, minWidth: `${width}px` }}
    >
      {children}
      <div
        onMouseDown={handleMouseDown}
        className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary/30 transition-colors"
      />
    </div>
  );
}

// ============================================================
// DRAGGABLE CREW ITEM - For the crew pool (compact, clickable)
// ============================================================
// DRAGGABLE CREW ITEM - For the crew pool (matches SHIFT Crew Planner exactly)
// ============================================================
function DraggableCrewItem({ staff, scale, onEditCrew }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `crew-${staff.id}`,
    data: { type: "crew", staff },
  });

  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined;

  // MET score display (matching SHIFT Crew Planner)
  const metScore = String(staff.preference_value || 0).padStart(2, "0");

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div
          ref={setNodeRef}
          style={{ ...style, height: `${Math.round(26 * scale)}px`, fontSize: `${Math.round(11 * scale)}px` }}
          {...listeners}
          {...attributes}
          className={cn(
            "flex items-center gap-1.5 px-2 mx-0.5 mb-0.5 rounded cursor-grab active:cursor-grabbing min-w-0 overflow-hidden",
            "bg-surface-elevated/30 border border-border/50 hover:bg-surface-elevated/50 hover:border-primary/30 transition-colors",
            isDragging && "opacity-50"
          )}
        >
          <GripVertical className="text-muted-foreground shrink-0" style={{ width: `${12 * scale}px`, height: `${12 * scale}px` }} />
          <span 
            className="text-muted-foreground shrink-0 font-mono" 
            style={{ fontSize: `${9 * scale}px`, minWidth: `${14 * scale}px` }}
            title="MET Score"
          >
            {metScore}
          </span>
          <User className="text-muted-foreground shrink-0" style={{ width: `${12 * scale}px`, height: `${12 * scale}px` }} />
          <span className="truncate flex-1 min-w-0">
            {/* EVENTSFLO-CREW-PLANNER-CREW-PICKER-SURNAME-SORT-AND-ASSIGNED-LIST-SCROLL-FIX-V1
                Crew Pool / left-side picker displays as "SURNAME, First name"
                (matches operational sort order). This formatting is scoped to
                the Crew Pool item; assigned-crew rows elsewhere continue to
                use the existing `staff_name` field. */}
            {(staff.last_name || "").trim()
              ? `${staff.last_name}, ${staff.first_name || ""}`.trim().replace(/,\s*$/, "")
              : (staff.first_name || "—")}
          </span>
          {/* EVENTSFLO-CREW-PLANNER-READINESS-VISUAL-V1: read-only readiness chip */}
          <CrewReadinessChip staff={staff} scale={scale} />
          {staff.skills?.includes("Crew Chief") && (
            <span style={{ fontSize: `${10 * scale}px` }}>⛑️</span>
          )}
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-40">
        <ContextMenuItem onClick={() => onEditCrew(staff.id)}>
          <Pencil className="h-4 w-4 mr-2" />
          Edit Crew
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}

// ============================================================
// RESIZABLE PANEL HANDLE
// ============================================================
function ResizeHandle({ onResize }) {
  const handleRef = useRef(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const handleMouseDown = (e) => {
    isDragging.current = true;
    startX.current = e.clientX;
    startWidth.current = handleRef.current?.parentElement?.offsetWidth || 160;
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    const delta = e.clientX - startX.current;
    const newWidth = Math.max(120, Math.min(300, startWidth.current + delta));
    onResize(newWidth);
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  };

  return (
    <div
      ref={handleRef}
      onMouseDown={handleMouseDown}
      className="absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-primary/30 transition-colors flex items-center justify-center group"
    >
      <GripHorizontal className="h-4 w-4 text-muted-foreground/30 group-hover:text-primary/50 rotate-90" />
    </div>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================
export default function SheetCrewPlannerPage() {
  const navigate = useNavigate();

  // Data state
  const [shifts, setShifts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [venues, setVenues] = useState([]);
  const [venueLocations, setVenueLocations] = useState([]);
  const [crewPool, setCrewPool] = useState([]);
  const [shiftCrewMap, setShiftCrewMap] = useState({});
  const [shiftLineItemsMap, setShiftLineItemsMap] = useState({});
  const [loading, setLoading] = useState(true);

  // EVENTSFLO-CREW-PLANNER-EMAIL-CREW-RESTORE-V1: modal context for ad-hoc email
  const [emailCrewContext, setEmailCrewContext] = useState(null);

  // Filter state
  const [projectFilter, setProjectFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [crewSearchTerm, setCrewSearchTerm] = useState("");
  const [crewSortOrder, setCrewSortOrder] = useState("preference");

  // EVENTSFLO-CLIENT-PORTAL-PHASE-2.2-CONVERTED-REQUEST-OPEN-IN-PLANNER-V1
  // Preselect project when arriving via ?project_id=<id> (e.g. from Client
  // Requests admin "Open in Planner" handoff). Apply once on mount, then
  // clear the query param so navigating away doesn't keep the filter.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const preset = searchParams.get("project_id");
    if (preset) {
      setProjectFilter(preset);
      const next = new URLSearchParams(searchParams);
      next.delete("project_id");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sort state
  // EF-SHEET-PLANNER-COLUMN-SORTING: 3-state toggle (asc → desc → null)
  // EVENTSFLO-CREW-PLANNER-DATE-FILTER-SORT-AND-COLUMN-WIDTH-V1:
  // Default sort is now **newest date first** so the operator always lands
  // on the most recent work without manually reversing the sort each time.
  const [sortField, setSortField] = useState("date");
  const [sortDirection, setSortDirection] = useState("desc");

  // UI state
  const [zoomLevel, setZoomLevel] = useState(() => {
    try {
      const saved = localStorage.getItem(ZOOM_STORAGE_KEY);
      if (saved && ZOOM_LEVELS.find(z => z.key === saved)) return saved;
    } catch {}
    return "md";
  });
  // EF-SHEET-UX-POLISH-PASS-01: Reduced panel width by 25% (160 -> 120)
  const [panelWidth, setPanelWidth] = useState(() => {
    try {
      const saved = localStorage.getItem(PANEL_WIDTH_KEY);
      if (saved) return parseInt(saved, 10);
    } catch {}
    return 120;
  });
  const [expandedRows, setExpandedRows] = useState([]); // Max 2 rows
  const [columnWidths, setColumnWidths] = useState(DEFAULT_COLUMN_WIDTHS);

  // Drag state
  const [activeId, setActiveId] = useState(null);

  // Local edit state for expanded rows (Explicit Save Model)
  const [localEdits, setLocalEdits] = useState({});

  // Clipboard state for copy/paste crew (matching SHIFT Crew Planner)
  const [crewClipboard, setCrewClipboard] = useState(null); // { sourceShiftId, crew: [{staff_id, role}...] }

  // EF-CREW-CONFLICT-PREVENTION-V1: Conflict dialog state
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [pendingConflicts, setPendingConflicts] = useState([]);
  const [conflictOverrideReason, setConflictOverrideReason] = useState("");
  const [pendingAssignment, setPendingAssignment] = useState(null);

  // EF-PLANNER-DEPRECATION-AND-RENAMING-V1: Visibility toggles
  const [hideCompletedShifts, setHideCompletedShifts] = useState(getStoredHideCompletedShifts);
  const [hideArchivedProjects, setHideArchivedProjects] = useState(getStoredHideArchivedProjects);

  // EVENTSFLO-CREW-PLANNER-DATE-FILTER-SORT-AND-COLUMN-WIDTH-V1:
  // Date-range display dropdown state — All | Today | Week | Month.
  // Default is "all" so future work is never hidden unexpectedly.
  const [displayRange, setDisplayRange] = useState(() => {
    try {
      const stored = localStorage.getItem(DISPLAY_RANGE_KEY);
      if (stored && DISPLAY_RANGE_OPTIONS.some((o) => o.value === stored)) {
        return stored;
      }
    } catch (_) { /* localStorage unavailable */ }
    return "all";
  });
  const handleDisplayRangeChange = useCallback((value) => {
    setDisplayRange(value);
    try {
      localStorage.setItem(DISPLAY_RANGE_KEY, value);
    } catch (_) { /* localStorage unavailable */ }
  }, []);

  // EVENTSFLO-CREW-PLANNER-DATE-FILTER-SORT-AND-COLUMN-WIDTH-V1:
  // Grid container ref so columns can proportionally grow to fill
  // available browser/window width without requiring manual expansion.
  const gridContainerRef = useRef(null);
  const [gridContainerWidth, setGridContainerWidth] = useState(0);
  useEffect(() => {
    const el = gridContainerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setGridContainerWidth(Math.floor(entry.contentRect.width));
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // EF-PLANNER-DEPRECATION-AND-RENAMING-V1: Onboarding override dialog state
  const [onboardingOverrideDialogOpen, setOnboardingOverrideDialogOpen] = useState(false);
  const [pendingOnboardingOverride, setPendingOnboardingOverride] = useState(null);
  const [onboardingOverrideSubmitting, setOnboardingOverrideSubmitting] = useState(false);

  // EF-PLANNER-DEPRECATION-AND-RENAMING-V1: Log active planner on mount
  useEffect(() => {
    console.log("[SHEET PLANNER ACTIVE] Crew Planner (SHEET) is now the active planner");
  }, []);

  // Get current zoom scale
  const currentZoom = ZOOM_LEVELS.find(z => z.key === zoomLevel) || ZOOM_LEVELS[2];
  const scale = currentZoom.scale;

  // Drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  // ============================================================
  // NAVIGATION HANDLERS (matching SHIFT Crew Planner)
  // ============================================================
  
  // Navigate to Project page
  const handleNavigateProject = useCallback((projectId) => {
    if (projectId) {
      navigate(`/projects/${projectId}`, { state: { returnUrl: '/planner/sheet' } });
    }
  }, [navigate]);

  // Navigate to Shift edit (via Project page with editShift param)
  const handleNavigateShift = useCallback((shiftId) => {
    const shift = shifts.find(s => s.id === shiftId);
    if (shift?.project_id) {
      navigate(`/projects/${shift.project_id}?editShift=${shiftId}`, { state: { returnUrl: '/planner/sheet' } });
    }
  }, [shifts, navigate]);

  // Navigate to Venue page
  const handleNavigateVenue = useCallback((venueId) => {
    if (venueId) {
      navigate(`/venues?edit=${venueId}`, { state: { returnUrl: '/planner/sheet' } });
    }
  }, [navigate]);

  // Navigate to Location (via Venue page)
  const handleNavigateLocation = useCallback((venueId, locationId) => {
    if (venueId && locationId) {
      navigate(`/venues?edit=${venueId}&location=${locationId}`, { state: { returnUrl: '/planner/sheet' } });
    }
  }, [navigate]);

  // Navigate to Crew profile (matching SHIFT Crew Planner exactly)
  const handleEditCrew = useCallback((staffId) => {
    navigate(`/staff/${staffId}/edit`, { state: { returnUrl: '/planner/sheet' } });
  }, [navigate]);

  // ============================================================
  // DATA LOADING
  // ============================================================
  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      const [shiftsRes, projectsRes, venuesRes, locationsRes, staffRes] = await Promise.all([
        getShifts({ limit: 500 }),
        getProjects({ limit: 200 }),
        getVenues({ limit: 200 }),
        getVenueLocations({ limit: 500 }),
        getStaffList({ status: "active", limit: 500 }),
      ]);

      const shiftsData = (shiftsRes.data || []).map((s, idx) => ({ ...s, _originalIndex: idx }));
      setShifts(shiftsData);
      setProjects(projectsRes.data || []);
      setVenues(venuesRes.data || []);
      setVenueLocations(locationsRes.data || []);
      setCrewPool(staffRes.data?.items || staffRes.data || []);

      // Load crew and line items for each shift
      const crewMap = {};
      const lineItemsMap = {};
      await Promise.all(
        shiftsData.map(async (shift) => {
          try {
            const [crewRes, lineItemsRes] = await Promise.all([
              getShiftCrew(shift.id),
              getShiftLineItems(shift.id),
            ]);
            crewMap[shift.id] = crewRes.data || [];
            // Line items endpoint returns { items: [...] }
            lineItemsMap[shift.id] = lineItemsRes.data?.items || lineItemsRes.data || [];
          } catch {
            crewMap[shift.id] = [];
            lineItemsMap[shift.id] = [];
          }
        })
      );
      setShiftCrewMap(crewMap);
      setShiftLineItemsMap(lineItemsMap);
    } catch (err) {
      toast.error("Failed to load data");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Save preferences
  useEffect(() => {
    localStorage.setItem(ZOOM_STORAGE_KEY, zoomLevel);
  }, [zoomLevel]);

  useEffect(() => {
    localStorage.setItem(PANEL_WIDTH_KEY, panelWidth.toString());
  }, [panelWidth]);

  // ============================================================
  // LOOKUP HELPERS
  // ============================================================
  const getProject = useCallback(
    (projectId) => projects.find((p) => p.id === projectId),
    [projects]
  );

  const getProjectName = useCallback(
    (projectId) => getProject(projectId)?.name || "Unknown",
    [getProject]
  );

  const getVenue = useCallback(
    (venueId) => venues.find((v) => v.id === venueId),
    [venues]
  );

  const getVenueName = useCallback(
    (venueId) => getVenue(venueId)?.name || "—",
    [getVenue]
  );

  const getLocation = useCallback(
    (locationId) => venueLocations.find((l) => l.id === locationId),
    [venueLocations]
  );

  const getLocationName = useCallback(
    (locationId) => getLocation(locationId)?.name || "—",
    [getLocation]
  );

  const getLocationAddress = useCallback(
    (locationId) => {
      const loc = getLocation(locationId);
      if (!loc) return "—";
      // Use address1, address2 (city), address3 fields from venue location
      const parts = [loc.address1, loc.address2].filter(Boolean);
      return parts.join(", ") || "—";
    },
    [getLocation]
  );

  // Sorted and filtered crew pool (matching SHIFT Crew Planner behavior)
  const sortedCrewPool = useMemo(() => {
    let result = [...crewPool];
    
    // Filter by search
    if (crewSearchTerm) {
      const lower = crewSearchTerm.toLowerCase();
      result = result.filter(
        (s) =>
          s.first_name?.toLowerCase().includes(lower) ||
          s.last_name?.toLowerCase().includes(lower)
      );
    }
    
    // Sort
    // EVENTSFLO-CREW-PLANNER-CREW-PICKER-SURNAME-SORT-AND-ASSIGNED-LIST-SCROLL-FIX-V1
    // name_asc / name_desc now sort by surname first, then first name —
    // matching the new "SURNAME, First name" display in the Crew Pool.
    // "preference" sort is unchanged.
    const surnameKey = (s) =>
      `${(s.last_name || "").toLocaleLowerCase()} ${(s.first_name || "").toLocaleLowerCase()}`.trim();
    switch (crewSortOrder) {
      case "preference":
        result.sort((a, b) => (b.preference_value || 0) - (a.preference_value || 0));
        break;
      case "name_asc":
        result.sort((a, b) => surnameKey(a).localeCompare(surnameKey(b)));
        break;
      case "name_desc":
        result.sort((a, b) => surnameKey(b).localeCompare(surnameKey(a)));
        break;
    }
    
    return result;
  }, [crewPool, crewSearchTerm, crewSortOrder]);

  // Filter and sort shifts
  // EF-PLANNER-DEPRECATION-AND-RENAMING-V1: Added visibility filtering
  // EVENTSFLO-CREW-PLANNER-DATE-FILTER-SORT-AND-COLUMN-WIDTH-V1: Added date-range display filter
  const filteredShifts = useMemo(() => {
    let result = [...shifts];

    // EVENTSFLO-CREW-PLANNER-DATE-FILTER-SORT-AND-COLUMN-WIDTH-V1:
    // Date-range display filter — see /app/frontend/src/lib/crewPlannerDateFilter.js.
    if (displayRange !== "all") {
      result = filterShiftsByDisplayRange(result, displayRange);
    }

    // EF-PLANNER-DEPRECATION-AND-RENAMING-V1: Filter completed shifts (end_time < now)
    if (hideCompletedShifts) {
      const now = new Date();
      result = result.filter(shift => {
        // Try to get end time or calculate from start_time + duration
        if (shift.end_time) {
          return new Date(shift.end_time) >= now;
        }
        // Fallback: use date field
        if (shift.date) {
          const shiftDate = new Date(shift.date);
          shiftDate.setHours(23, 59, 59, 999); // End of day
          return shiftDate >= now;
        }
        // If no date info, include the shift
        return true;
      });
    }
    
    // EF-PLANNER-DEPRECATION-AND-RENAMING-V1: Filter archived projects
    if (hideArchivedProjects) {
      result = result.filter(shift => {
        const projectStatus = shift.project_status?.toLowerCase();
        if (!projectStatus) return true; // Keep if no status
        return !["completed", "archived"].includes(projectStatus);
      });
    }

    // Project filter
    if (projectFilter !== "all") {
      result = result.filter((s) => s.project_id === projectFilter);
    }

    // Search filter
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      result = result.filter(
        (s) =>
          s.name?.toLowerCase().includes(lower) ||
          getProjectName(s.project_id).toLowerCase().includes(lower) ||
          getVenueName(s.venue_id).toLowerCase().includes(lower)
      );
    }

    // EF-SHEET-PLANNER-COLUMN-SORTING: Sort with 3-state toggle
    // null sortField = restore original dataset order via _originalIndex
    if (!sortField || !sortDirection) {
      // Reset state: sort by original load order
      result.sort((a, b) => (a._originalIndex ?? 0) - (b._originalIndex ?? 0));
    } else if (sortField && sortDirection) {
      result.sort((a, b) => {
        let aVal, bVal;
        switch (sortField) {
          case "project":
            aVal = getProjectName(a.project_id);
            bVal = getProjectName(b.project_id);
            break;
          case "shift":
            aVal = a.name || "";
            bVal = b.name || "";
            break;
          case "date":
            aVal = a.date || "";
            bVal = b.date || "";
            break;
          case "venue":
            aVal = getVenueName(a.venue_id);
            bVal = getVenueName(b.venue_id);
            break;
          case "location":
            aVal = getLocationName(a.venue_location_id);
            bVal = getLocationName(b.venue_location_id);
            break;
          case "address":
            aVal = getLocationAddress(a.venue_location_id);
            bVal = getLocationAddress(b.venue_location_id);
            break;
          case "start":
            aVal = a.start_time || "";
            bVal = b.start_time || "";
            break;
          case "end":
            aVal = a.end_time || "";
            bVal = b.end_time || "";
            break;
          case "hours":
            // Numeric sort for hours
            aVal = parseFloat(a.duration_hours) || 0;
            bVal = parseFloat(b.duration_hours) || 0;
            return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
          case "crew":
            // Numeric sort for crew count
            aVal = parseInt(a.staff_required) || 0;
            bVal = parseInt(b.staff_required) || 0;
            return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
          default:
            aVal = "";
            bVal = "";
        }
        // String comparison for text columns
        const cmp = String(aVal).localeCompare(String(bVal));
        return sortDirection === "asc" ? cmp : -cmp;
      });
    }

    return result;
  }, [shifts, projectFilter, searchTerm, sortField, sortDirection, getProjectName, getVenueName, getLocationName, getLocationAddress, hideCompletedShifts, hideArchivedProjects, displayRange]);

  // EF-PLANNER-DEPRECATION-AND-RENAMING-V1: Toggle handlers for visibility controls
  const handleToggleHideCompletedShifts = useCallback((checked) => {
    setHideCompletedShifts(checked);
    setStoredHideCompletedShifts(checked);
  }, []);
  
  const handleToggleHideArchivedProjects = useCallback((checked) => {
    setHideArchivedProjects(checked);
    setStoredHideArchivedProjects(checked);
  }, []);

  // Handle row expansion (max 2 rows)
  const handleRowToggle = useCallback((shiftId) => {
    setExpandedRows((prev) => {
      if (prev.includes(shiftId)) {
        return prev.filter((id) => id !== shiftId);
      } else {
        const newExpanded = [...prev, shiftId];
        if (newExpanded.length > 2) {
          newExpanded.shift();
        }
        return newExpanded;
      }
    });
  }, []);

  // Handle sort toggle - EF-SHEET-PLANNER-COLUMN-SORTING: 3-state toggle
  // 1st click → Ascending, 2nd click → Descending, 3rd click → Original order
  const handleSort = useCallback((field) => {
    if (sortField !== field) {
      // Clicking a new column: start with ascending
      setSortField(field);
      setSortDirection("asc");
    } else if (sortDirection === "asc") {
      // Same column, currently asc: switch to desc
      setSortDirection("desc");
    } else if (sortDirection === "desc") {
      // Same column, currently desc: reset to original order
      setSortField(null);
      setSortDirection(null);
    } else {
      // Fallback: start with ascending
      setSortField(field);
      setSortDirection("asc");
    }
  }, [sortField, sortDirection]);

  // Handle column resize
  const handleColumnResize = useCallback((column, width) => {
    setColumnWidths(prev => ({ ...prev, [column]: width }));
  }, []);

  // Handle zoom
  const handleZoomIn = () => {
    const idx = ZOOM_LEVELS.findIndex(z => z.key === zoomLevel);
    if (idx < ZOOM_LEVELS.length - 1) {
      setZoomLevel(ZOOM_LEVELS[idx + 1].key);
    }
  };

  const handleZoomOut = () => {
    const idx = ZOOM_LEVELS.findIndex(z => z.key === zoomLevel);
    if (idx > 0) {
      setZoomLevel(ZOOM_LEVELS[idx - 1].key);
    }
  };

  // ============================================================
  // CREW ASSIGNMENT HANDLERS (immediate persistence)
  // EF-CREW-CONFLICT-PREVENTION-V1: Added conflict detection
  // EF-PLANNER-DEPRECATION-AND-RENAMING-V1: Added onboarding override support
  // ============================================================
  const handleAddCrew = useCallback(async (shiftId, staffId, role = "Crew", overrideConflicts = false, overrideReason = null, onboardingOverride = false) => {
    try {
      await addShiftCrew(shiftId, staffId, role, null, overrideConflicts, overrideReason, onboardingOverride);
      const crewRes = await getShiftCrew(shiftId);
      setShiftCrewMap((prev) => ({ ...prev, [shiftId]: crewRes.data || [] }));
      toast.success("Crew member assigned");
      
      // Reset conflict state on success
      setConflictDialogOpen(false);
      setPendingConflicts([]);
      setConflictOverrideReason("");
      setPendingAssignment(null);
      
      // Reset onboarding override state
      setOnboardingOverrideDialogOpen(false);
      setPendingOnboardingOverride(null);
    } catch (err) {
      const status = err.response?.status;
      const errorData = err.response?.data?.detail || err.response?.data;
      
      // EF-CREW-CONFLICT-PREVENTION-V1: Handle conflict error
      if (typeof errorData === 'object' && errorData?.error_type === 'SHIFT_CONFLICT') {
        setPendingConflicts(errorData.conflicts || []);
        setPendingAssignment({ shiftId, staffId, role });
        setConflictDialogOpen(true);
        return;
      }
      
      // EF-PLANNER-DEPRECATION-AND-RENAMING-V1: Handle crew_not_active with can_override
      if (status === 403 && typeof errorData === 'object' && errorData?.error === 'crew_not_active' && errorData?.can_override) {
        console.log("[OVERRIDE DIALOG TRIGGERED]", { staffId, shiftId, reason: errorData?.reason });
        // Find staff name from crew pool
        const staff = crewPool.find(c => c.id === staffId);
        setPendingOnboardingOverride({ 
          shiftId, 
          staffId, 
          role,
          staffName: errorData?.crew_name || errorData?.staff_name || (staff ? `${staff.first_name} ${staff.last_name}` : 'Unknown'),
          reason: errorData?.reason || errorData?.message?.replace('Crew member is not active and cannot be assigned. Reason: ', '') || 'Missing required documents'
        });
        setOnboardingOverrideDialogOpen(true);
        return;
      }
      
      toast.error(extractErrorMessage(err));
    }
  }, [crewPool]);

  // EF-CREW-CONFLICT-PREVENTION-V1: Handle conflict override
  const handleConflictOverride = useCallback(async () => {
    if (!conflictOverrideReason.trim()) {
      toast.error("Please provide a reason for overriding the conflict");
      return;
    }
    
    if (pendingAssignment) {
      await handleAddCrew(
        pendingAssignment.shiftId, 
        pendingAssignment.staffId, 
        pendingAssignment.role,
        true, 
        conflictOverrideReason.trim()
      );
    }
  }, [conflictOverrideReason, pendingAssignment, handleAddCrew]);

  // EF-PLANNER-DEPRECATION-AND-RENAMING-V1: Onboarding override handlers
  const handleOnboardingOverride = useCallback(async () => {
    if (!pendingOnboardingOverride) return;
    
    setOnboardingOverrideSubmitting(true);
    console.log("[OVERRIDE CONFIRMED]", pendingOnboardingOverride);
    
    try {
      await handleAddCrew(
        pendingOnboardingOverride.shiftId,
        pendingOnboardingOverride.staffId,
        pendingOnboardingOverride.role,
        false,
        null,
        true // onboardingOverride = true
      );
    } finally {
      setOnboardingOverrideSubmitting(false);
    }
  }, [pendingOnboardingOverride, handleAddCrew]);

  const handleCloseOnboardingOverrideDialog = useCallback(() => {
    setOnboardingOverrideDialogOpen(false);
    setPendingOnboardingOverride(null);
  }, []);

  const handleRemoveCrew = useCallback(async (shiftId, staffId) => {
    try {
      await removeShiftCrew(shiftId, staffId);
      const crewRes = await getShiftCrew(shiftId);
      setShiftCrewMap((prev) => ({ ...prev, [shiftId]: crewRes.data || [] }));
      toast.success("Crew member removed");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }, []);

  const handleToggleCrewChief = useCallback(async (shiftId, staffId, makeCrewChief) => {
    const crew = shiftCrewMap[shiftId] || [];
    const assignment = crew.find((c) => c.staff_id === staffId);
    if (!assignment) return;

    let newRole = assignment.role || "Crew";
    newRole = newRole.replace(/\s*\(Crew Chief\)/gi, "").replace(/\s*\(Team Leader\)/gi, "").trim();
    if (newRole.toLowerCase() === "crew chief" || newRole.toLowerCase() === "team leader") {
      newRole = "Crew";
    }
    if (makeCrewChief) {
      newRole = `${newRole} (Crew Chief)`;
    }

    try {
      await updateShiftCrew(shiftId, staffId, newRole);
      const crewRes = await getShiftCrew(shiftId);
      setShiftCrewMap((prev) => ({ ...prev, [shiftId]: crewRes.data || [] }));
      toast.success(makeCrewChief ? "Set as Crew Chief" : "Removed Crew Chief status");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }, [shiftCrewMap]);

  // ============================================================
  // COPY/PASTE CREW HANDLERS (using shared module)
  // ============================================================
  const onCopyCrew = useCallback((shiftId) => {
    handleCopyCrew({ shiftId, shiftCrewMap, setCrewClipboard, shifts });
  }, [shiftCrewMap, shifts]);

  // EVENTSFLO-CREW-PLANNER-ASSIGNMENT-OVERRIDE-FOR-INCOMPLETE-ONBOARDING-V1
  // Batch override state for the Paste Crew flow. When handlePasteCrew
  // returns status="needs_override", we open a single confirmation dialog
  // that lists every crew member blocked only by incomplete onboarding /
  // missing or expired documents, then commit the override in one click.
  const [pasteOverridePending, setPasteOverridePending] = useState(null);

  const onPasteCrew = useCallback(async (targetShiftId) => {
    const result = await handlePasteCrew({
      targetShiftId,
      crewClipboard,
      shifts,
      shiftCrewMap,
      loadData,
    });
    if (result && result.status === "needs_override") {
      setPasteOverridePending({ targetShiftId, ...result });
    }
  }, [crewClipboard, shifts, shiftCrewMap, loadData]);

  const handleConfirmPasteOverride = useCallback(async () => {
    if (!pasteOverridePending) return;
    const pending = pasteOverridePending;
    setPasteOverridePending(null); // close dialog optimistically
    await commitPasteOverride({
      targetShiftId: pending.targetShiftId,
      pending,
      loadData,
    });
  }, [pasteOverridePending, loadData]);

  const handleCancelPasteOverride = useCallback(async () => {
    if (!pasteOverridePending) return;
    const pending = pasteOverridePending;
    setPasteOverridePending(null);
    // Reload anyway so any already-added crew show up; finish the toast.
    try { await loadData(); } catch { /* noop */ }
    const summary =
      `Added ${pending.added || 0}` +
      (pending.alreadyAssigned ? ` · ${pending.alreadyAssigned} already assigned` : "") +
      ` · ${pending.overridable.length} skipped (override declined)` +
      (pending.failedReasons?.length ? ` · ${pending.failedReasons.length} failed` : "");
    toast.info(summary, { id: pending.toastId, duration: 6000 });
  }, [pasteOverridePending, loadData]);

  // ============================================================
  // CREW MEMBER CONTEXT MENU HANDLERS (EF-SHEET-CREW-MEMBER-CONTEXT-MENU-PARITY)
  // ============================================================
  const handleToggleConfirmed = useCallback(async (shiftId, staffId, confirmed) => {
    try {
      // EVENTSFLO-CREW-PLANNER-CONFIRMED-STATUS-AND-SHIFT-NAVIGATION-FIX-V1
      // Pass the patch object directly — the helper detects an object
      // and forwards it as the body. Prior wiring passed the patch as
      // the `notes` arg which produced a `notes: Input should be a
      // valid string` validation error from Pydantic.
      await updateShiftCrew(shiftId, staffId, { confirmed });
      const crewRes = await getShiftCrew(shiftId);
      setShiftCrewMap((prev) => ({ ...prev, [shiftId]: crewRes.data || [] }));
      toast.success(confirmed ? "Marked as confirmed" : "Unmarked confirmed");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }, []);

  const handleToggleWorked = useCallback(async (shiftId, staffId, worked) => {
    try {
      // EVENTSFLO-CREW-PLANNER-CONFIRMED-STATUS-AND-SHIFT-NAVIGATION-FIX-V1
      await updateShiftCrew(shiftId, staffId, { worked });
      const crewRes = await getShiftCrew(shiftId);
      setShiftCrewMap((prev) => ({ ...prev, [shiftId]: crewRes.data || [] }));
      toast.success(worked ? "Marked as worked" : "Unmarked worked");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }, []);

  // EVENTSFLO-CREW-PWA-LINK-SEND-AND-PENDING-ACTIONS-RESTORE-V1
  // Send the staff-scoped PWA launch URL by email; surface a clear
  // missing-email message when the crew has no email on file.
  const handleSendPWALink = useCallback(async (staffId, staffName) => {
    if (!staffId) {
      toast.error("Cannot send: missing crew id.");
      return;
    }
    try {
      const res = await sendStaffPWALink(staffId);
      const data = res?.data || {};
      if (data.email_sent === false) {
        toast.warning(
          `${staffName ? staffName + " — " : ""}Email provider not configured. Link recorded only.`,
          { description: data.pwa_link },
        );
        return;
      }
      toast.success(`PWA install link sent to ${staffName || data.staff_id}.`);
    } catch (err) {
      const msg = extractErrorMessage(err);
      toast.error(msg || "Failed to send PWA install link.");
    }
  }, []);

  const handleCopyPWAInstall = useCallback(async (staffId, staffName) => {
    if (!staffId) {
      toast.error("Cannot copy: missing crew id.");
      return;
    }
    // Crew-specific Portal launch URL (matches the route mounted in App.js).
    // EVENTSFLO-CREW-PORTAL-SHARE-LINK-STAFF-FILTER-V1: target the active
    // CrewPortalPage at /crew (was /crew-pwa/jobs before the deprecated tree
    // was removed). The `staff` query param is consumed by CrewPortalPage.
    const pwaUrl = `${window.location.origin}/crew?staff=${staffId}`;
    const message = `Hi ${staffName || "there"}! Open the EventsFlo crew app: ${pwaUrl}`;
    try {
      await navigator.clipboard.writeText(message);
      toast.success("PWA install link copied to clipboard");
    } catch (err) {
      toast.error("Could not copy link — please copy manually.", {
        description: pwaUrl,
      });
    }
  }, []);

  const handleEmailCrew = useCallback((shiftId, staffId, staffName) => {
    // EVENTSFLO-CREW-PLANNER-EMAIL-CREW-RESTORE-V1
    const shift = shifts.find((s) => s.id === shiftId);
    if (!shift) {
      toast.error("Shift not found — refresh and try again.");
      return;
    }
    const crewRow = (shiftCrewMap[shiftId] || []).find(
      (a) => a.staff_id === staffId,
    );
    const fullStaff = crewPool.find((s) => s.id === staffId);
    const staffEmail = crewRow?.staff_email || fullStaff?.email || null;
    if (!staffEmail) {
      toast.error("Crew member has no email address.");
      return;
    }
    const venue = venues.find((v) => v.id === shift.venue_id);
    const loc = venueLocations.find((l) => l.id === shift.venue_location_id);
    const venueLabel = [venue?.name, loc?.name].filter(Boolean).join(" — ");
    setEmailCrewContext({
      shift,
      staffId,
      staffName,
      staffEmail,
      venueLabel,
    });
  }, [shifts, shiftCrewMap, crewPool, venues, venueLocations]);

  // EVENTSFLO-CREW-PLANNER-PUSH-TO-APP-RESTORE-V1
  // Wire the assigned-crew context-menu "Push to App" action to the
  // existing backend endpoint POST /api/shifts/{shift_id}/crew/{staff_id}/push.
  // The endpoint sets the assignment status to "pending" and stamps
  // pushed_at; the crew member then accepts/rejects from the PWA Crew Jobs
  // page (existing flow). Failures surface a clear admin-facing error.
  const handlePushCrew = useCallback(async (shiftId, staffId, staffName) => {
    if (!shiftId || !staffId) {
      toast.error("Cannot push: missing shift or crew id.");
      return;
    }
    try {
      await pushShiftTaskToCrew(shiftId, staffId, false);
      toast.success(`Shift request sent to ${staffName} — they can accept or reject from the app.`);
      // Refresh the assigned-crew row so the UI shows the new "pending" status.
      const crewRes = await getShiftCrew(shiftId).catch(() => null);
      if (crewRes?.data) {
        setShiftCrewMap((prev) => ({ ...prev, [shiftId]: crewRes.data || [] }));
      }
    } catch (err) {
      const msg = extractErrorMessage(err);
      // Map the most common backend rejections to admin-friendly copy.
      const lower = String(msg || "").toLowerCase();
      if (lower.includes("not assigned")) {
        toast.error("Cannot push — crew member is not assigned to this shift.");
      } else if (lower.includes("staff member not found") || lower.includes("inactive")) {
        toast.error("Crew member cannot receive app request — no PWA account/contact method.");
      } else {
        toast.error(`Push to App failed: ${msg || "unknown error"}`);
      }
    }
  }, []);

  // ============================================================
  // LOCAL EDIT HANDLERS (Explicit Save Model)
  // ============================================================
  const handleLocalEdit = useCallback((shiftId, field, value) => {
    setLocalEdits((prev) => ({
      ...prev,
      [shiftId]: {
        ...prev[shiftId],
        [field]: value,
        _dirty: true,
      },
    }));
  }, []);

  const handleSaveShift = useCallback(async (shiftId) => {
    const edits = localEdits[shiftId];
    if (!edits || !edits._dirty) {
      toast.info("No changes to save");
      return;
    }

    const shift = shifts.find((s) => s.id === shiftId);
    if (!shift) return;

    const payload = {};
    if (edits.start_time !== undefined) payload.start_time = edits.start_time;
    if (edits.end_time !== undefined) payload.end_time = edits.end_time;
    if (edits.venue_location_id !== undefined) payload.venue_location_id = edits.venue_location_id;
    if (edits.staff_required !== undefined) payload.staff_required = parseInt(edits.staff_required, 10);
    if (edits.notes !== undefined) payload.notes = edits.notes;

    if (payload.start_time || payload.end_time) {
      const startTime = payload.start_time || shift.start_time;
      const endTime = payload.end_time || shift.end_time;
      if (startTime && endTime) {
        const [sh, sm] = startTime.split(":").map(Number);
        const [eh, em] = endTime.split(":").map(Number);
        let startMins = sh * 60 + sm;
        let endMins = eh * 60 + em;
        if (endMins <= startMins) endMins += 24 * 60;
        payload.duration_hours = (endMins - startMins) / 60;
      }
    }

    try {
      await updateShiftWithRules(shiftId, payload);
      setLocalEdits((prev) => {
        const next = { ...prev };
        delete next[shiftId];
        return next;
      });
      await loadData();
      toast.success("Shift saved successfully");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }, [localEdits, shifts, loadData]);

  const handleCancelEdit = useCallback((shiftId) => {
    setLocalEdits((prev) => {
      const next = { ...prev };
      delete next[shiftId];
      return next;
    });
  }, []);

  // ============================================================
  // DRAG HANDLERS
  // ============================================================
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    if (activeData?.type === "crew" && overData?.type === "shift-drop") {
      const staff = activeData.staff;
      const shiftId = overData.shiftId;
      await handleAddCrew(shiftId, staff.id, "Crew");
    }
  };

  // Get active crew for drag overlay
  const activeCrew = activeId
    ? crewPool.find((s) => `crew-${s.id}` === activeId)
    : null;

  // EF-SHEET-PLANNER-COLUMN-SORTING: Column header with 3-state sort indicator
  const SortableHeader = ({ field, label }) => {
    const isActive = sortField === field;
    const isAsc = isActive && sortDirection === "asc";
    const isDesc = isActive && sortDirection === "desc";
    
    return (
      <button
        onClick={() => handleSort(field)}
        className={cn(
          "flex items-center gap-0.5 text-left font-medium transition-colors truncate w-full",
          isActive ? "text-primary" : "hover:text-primary"
        )}
        title={`Sort by ${label} (${isAsc ? "ascending" : isDesc ? "descending" : "click to sort"})`}
      >
        <span className="truncate">{label}</span>
        {isActive && (
          isAsc ? (
            <ChevronUp style={{ width: `${10 * scale}px`, height: `${10 * scale}px` }} className="shrink-0 text-primary" />
          ) : isDesc ? (
            <ChevronDown style={{ width: `${10 * scale}px`, height: `${10 * scale}px` }} className="shrink-0 text-primary" />
          ) : null
        )}
        {!isActive && (
          <div 
            style={{ width: `${10 * scale}px`, height: `${10 * scale}px` }} 
            className="shrink-0 opacity-0 group-hover:opacity-30"
          />
        )}
      </button>
    );
  };

  // Format currency for tooltip
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "–";
    return `€${amount.toFixed(2)}`;
  };

  // Row height based on zoom
  const headerHeight = Math.round(24 * scale);
  const fontSize = Math.round(11 * scale);

  // EVENTSFLO-CREW-PLANNER-DATE-FILTER-SORT-AND-COLUMN-WIDTH-V1:
  // Effective column widths that proportionally fill the available grid
  // container width. Growable text columns (project/shift/venue/location/
  // address) share any extra pixels; narrow numeric/time columns stay
  // compact. User manual resizes still win because they raise the base
  // width in `columnWidths` before this scaling is applied.
  const effectiveColumnWidths = useMemo(() => {
    const base = columnWidths || DEFAULT_COLUMN_WIDTHS;
    const baseSum = Object.values(base).reduce((a, b) => a + b, 0);
    const buffer = 4; // avoid horizontal scrollbar
    if (!gridContainerWidth || gridContainerWidth <= baseSum + buffer) {
      return base;
    }
    const extra = gridContainerWidth - baseSum - buffer;
    const growableSum = GROWABLE_COLUMNS.reduce((a, k) => a + (base[k] || 0), 0);
    if (growableSum <= 0) return base;
    const out = { ...base };
    GROWABLE_COLUMNS.forEach((k) => {
      const share = (base[k] || 0) / growableSum;
      out[k] = Math.floor((base[k] || 0) + extra * share);
    });
    return out;
  }, [columnWidths, gridContainerWidth]);

  // Calculate total grid width
  const totalWidth = Object.values(effectiveColumnWidths).reduce((a, b) => a + b, 0);

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="h-full flex flex-col" data-testid="sheet-crew-planner">
        {/* Header - Compact */}
        {/* EF-PLANNER-DEPRECATION-AND-RENAMING-V1: Updated header with "Crew Planner" and active label */}
        <div 
          className="flex items-center justify-between px-3 border-b border-border/50 bg-card/50"
          style={{ height: `${Math.round(36 * scale)}px` }}
        >
          <div className="flex items-center gap-2">
            <TableProperties style={{ width: `${16 * scale}px`, height: `${16 * scale}px` }} className="text-primary" />
            <h1 style={{ fontSize: `${14 * scale}px` }} className="font-semibold">Crew Planner</h1>
            <Badge variant="default" className="bg-green-600 text-white" style={{ fontSize: `${8 * scale}px`, height: `${16 * scale}px` }}>
              SHEET (Active)
            </Badge>
            <Badge variant="outline" style={{ fontSize: `${9 * scale}px`, height: `${18 * scale}px` }}>
              {filteredShifts.length} shifts
            </Badge>
          </div>

          <div className="flex items-center gap-2">
            {/* EVENTSFLO-CREW-PLANNER-DATE-FILTER-SORT-AND-COLUMN-WIDTH-V1:
                Date-range Display dropdown — sits to the LEFT of Hide Completed. */}
            <div
              className="flex items-center gap-1 px-2 border-r border-border/30"
              data-testid="crew-planner-display-range"
            >
              <label
                htmlFor="crew-planner-display-range-select"
                className="text-muted-foreground select-none"
                style={{ fontSize: `${9 * scale}px` }}
              >
                Display:
              </label>
              <select
                id="crew-planner-display-range-select"
                data-testid="crew-planner-display-range-select"
                value={displayRange}
                onChange={(e) => handleDisplayRangeChange(e.target.value)}
                className="rounded border border-border/30 bg-surface-base px-1 focus:outline-none focus:border-primary/50 cursor-pointer"
                style={{ height: `${18 * scale}px`, fontSize: `${9 * scale}px` }}
              >
                {DISPLAY_RANGE_OPTIONS.map((opt) => (
                  <option
                    key={opt.value}
                    value={opt.value}
                    data-testid={`crew-planner-display-range-option-${opt.value}`}
                  >
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* EF-PLANNER-DEPRECATION-AND-RENAMING-V1: Visibility toggles */}
            <div className="flex items-center gap-1.5 px-2 border-r border-border/30" data-testid="hide-completed-shifts-toggle">
              <Checkbox
                id="hide-completed-shifts"
                checked={hideCompletedShifts}
                onCheckedChange={handleToggleHideCompletedShifts}
                style={{ width: `${12 * scale}px`, height: `${12 * scale}px` }}
              />
              <label
                htmlFor="hide-completed-shifts"
                className="text-muted-foreground cursor-pointer select-none flex items-center gap-1"
                style={{ fontSize: `${9 * scale}px` }}
              >
                {hideCompletedShifts ? <EyeOff style={{ width: `${10 * scale}px`, height: `${10 * scale}px` }} /> : <Eye style={{ width: `${10 * scale}px`, height: `${10 * scale}px` }} />}
                Hide Completed
              </label>
            </div>
            
            <div className="flex items-center gap-1 px-2 border-r border-border/30" data-testid="hide-archived-projects-toggle">
              <Switch
                id="hide-archived-projects"
                checked={hideArchivedProjects}
                onCheckedChange={handleToggleHideArchivedProjects}
                style={{ transform: `scale(${scale * 0.7})` }}
              />
              <label
                htmlFor="hide-archived-projects"
                className="text-muted-foreground cursor-pointer select-none flex items-center gap-1"
                style={{ fontSize: `${9 * scale}px` }}
              >
                {hideArchivedProjects ? <EyeOff style={{ width: `${10 * scale}px`, height: `${10 * scale}px` }} /> : <Eye style={{ width: `${10 * scale}px`, height: `${10 * scale}px` }} />}
                Hide Archived
              </label>
            </div>

            {/* Zoom controls */}
            <div className="flex items-center gap-0.5 border border-border/50 rounded px-1" style={{ height: `${24 * scale}px` }}>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleZoomOut}
                disabled={zoomLevel === ZOOM_LEVELS[0].key}
                className="p-0"
                style={{ width: `${20 * scale}px`, height: `${20 * scale}px` }}
              >
                <ZoomOut style={{ width: `${12 * scale}px`, height: `${12 * scale}px` }} />
              </Button>
              <span style={{ fontSize: `${10 * scale}px`, width: `${32 * scale}px` }} className="text-center">{currentZoom.label}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleZoomIn}
                disabled={zoomLevel === ZOOM_LEVELS[ZOOM_LEVELS.length - 1].key}
                className="p-0"
                style={{ width: `${20 * scale}px`, height: `${20 * scale}px` }}
              >
                <ZoomIn style={{ width: `${12 * scale}px`, height: `${12 * scale}px` }} />
              </Button>
            </div>

            <Button variant="outline" size="sm" onClick={loadData} disabled={loading} style={{ height: `${24 * scale}px`, fontSize: `${10 * scale}px` }}>
              <RefreshCw className={cn(loading && "animate-spin")} style={{ width: `${12 * scale}px`, height: `${12 * scale}px`, marginRight: `${4 * scale}px` }} />
              Refresh
            </Button>

            {/* EVENTSFLO-UI-HELP-BUTTON-CREW-PLANNER-V1 */}
            <PageHelpButton
              testId="crew-planner"
              title="Crew Planner — Help"
              description="Spreadsheet-style planner for assigning crew to shifts. Click the help bullets below for usage rules and limitations."
              sections={CREW_PLANNER_HELP_SECTIONS}
              warnings={CREW_PLANNER_HELP_WARNINGS}
            />

            {/* EVENTSFLO-CREW-PWA-MESSAGE-CENTRE-V1 */}
            <AdminMessagesBell />
          </div>
        </div>

        {/* Filters - Compact */}
        <div 
          className="flex items-center gap-2 px-2 border-b border-border/30 bg-surface-elevated/30"
          style={{ height: `${Math.round(28 * scale)}px` }}
        >
          <div className="relative flex-1 max-w-[200px]">
            <Search 
              className="absolute text-muted-foreground" 
              style={{ left: `${6 * scale}px`, top: '50%', transform: 'translateY(-50%)', width: `${12 * scale}px`, height: `${12 * scale}px` }} 
            />
            <input
              type="text"
              placeholder="Search shifts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded border border-border/50 bg-surface-base focus:outline-none focus:border-primary/50"
              style={{ height: `${20 * scale}px`, paddingLeft: `${22 * scale}px`, fontSize: `${10 * scale}px` }}
            />
          </div>

          <Select value={projectFilter} onValueChange={setProjectFilter}>
            <SelectTrigger style={{ width: `${140 * scale}px`, height: `${20 * scale}px`, fontSize: `${10 * scale}px` }}>
              <SelectValue placeholder="All Projects" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" style={{ fontSize: `${10 * scale}px` }}>All Projects</SelectItem>
              {projects.map((p) => (
                <SelectItem key={p.id} value={p.id} style={{ fontSize: `${10 * scale}px` }}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Main content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Crew Pool Sidebar - Resizable */}
          <div 
            className="border-r border-border/50 flex flex-col bg-surface-elevated/5 relative"
            style={{ width: `${panelWidth}px` }}
          >
            {/* Panel Header */}
            <div 
              className="px-1.5 bg-surface-elevated text-white flex items-center justify-between"
              style={{ height: `${headerHeight}px`, fontSize: `${10 * scale}px` }}
            >
              <span className="flex items-center gap-1">
                <span style={{ fontSize: `${8 * scale}px` }}>👥</span>
                Crew Pool
              </span>
              <span 
                className="bg-white/10 px-1 rounded"
                style={{ fontSize: `${9 * scale}px` }}
              >
                {sortedCrewPool.length}
              </span>
            </div>
            
            {/* Search + Sort */}
            <div className="p-1 border-b border-border/30 space-y-0.5">
              <div className="relative">
                <Search 
                  className="absolute text-muted-foreground" 
                  style={{ left: `${4 * scale}px`, top: '50%', transform: 'translateY(-50%)', width: `${10 * scale}px`, height: `${10 * scale}px` }} 
                />
                <input
                  type="text"
                  placeholder="Search crew..."
                  value={crewSearchTerm}
                  onChange={(e) => setCrewSearchTerm(e.target.value)}
                  className="w-full rounded border border-border/30 bg-surface-base focus:outline-none focus:border-primary/50"
                  style={{ height: `${18 * scale}px`, paddingLeft: `${18 * scale}px`, fontSize: `${9 * scale}px` }}
                  data-testid="crew-search"
                />
              </div>
              
              <div className="flex items-center gap-1">
                <span style={{ fontSize: `${8 * scale}px` }} className="text-muted-foreground shrink-0">Order:</span>
                <select
                  value={crewSortOrder}
                  onChange={(e) => setCrewSortOrder(e.target.value)}
                  className="flex-1 px-1 rounded border border-border/30 bg-surface-base focus:outline-none focus:border-primary/50 cursor-pointer"
                  style={{ height: `${16 * scale}px`, fontSize: `${9 * scale}px` }}
                  data-testid="crew-sort"
                >
                  {SORT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Crew List */}
            <ScrollArea className="flex-1">
              {loading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="animate-spin text-muted-foreground" style={{ width: `${16 * scale}px`, height: `${16 * scale}px` }} />
                </div>
              ) : sortedCrewPool.length === 0 ? (
                <div className="p-2 text-muted-foreground text-center" style={{ fontSize: `${9 * scale}px` }}>
                  No active crew found
                </div>
              ) : (
                sortedCrewPool.map((staff) => (
                  <DraggableCrewItem 
                    key={staff.id} 
                    staff={staff} 
                    scale={scale}
                    onEditCrew={handleEditCrew}
                  />
                ))
              )}
            </ScrollArea>

            {/* Resize Handle */}
            <ResizeHandle onResize={setPanelWidth} />
          </div>

          {/* Spreadsheet Grid */}
          <div className="flex-1 flex flex-col overflow-hidden" ref={gridContainerRef}>
            {/* Column Headers - Resizable */}
            <div
              className="flex border-b border-border/50 bg-surface-elevated/50 text-muted-foreground shrink-0 overflow-x-auto"
              style={{ height: `${headerHeight}px`, fontSize: `${fontSize}px` }}
            >
              <ResizableColumnHeader 
                width={effectiveColumnWidths.project} 
                onResize={(w) => handleColumnResize('project', w)}
                className="px-1 border-r border-border/30"
              >
                <SortableHeader field="project" label="Project" />
              </ResizableColumnHeader>
              <ResizableColumnHeader 
                width={effectiveColumnWidths.shift} 
                onResize={(w) => handleColumnResize('shift', w)}
                className="px-1 border-r border-border/30"
              >
                <SortableHeader field="shift" label="Shift" />
              </ResizableColumnHeader>
              <ResizableColumnHeader 
                width={effectiveColumnWidths.venue} 
                onResize={(w) => handleColumnResize('venue', w)}
                className="px-1 border-r border-border/30"
              >
                <SortableHeader field="venue" label="Venue" />
              </ResizableColumnHeader>
              <ResizableColumnHeader 
                width={effectiveColumnWidths.location} 
                onResize={(w) => handleColumnResize('location', w)}
                className="px-1 border-r border-border/30"
              >
                <SortableHeader field="location" label="Location" />
              </ResizableColumnHeader>
              <ResizableColumnHeader 
                width={effectiveColumnWidths.date} 
                onResize={(w) => handleColumnResize('date', w)}
                className="px-1 border-r border-border/30 justify-center"
              >
                <SortableHeader field="date" label="Date" />
              </ResizableColumnHeader>
              <ResizableColumnHeader 
                width={effectiveColumnWidths.start} 
                onResize={(w) => handleColumnResize('start', w)}
                className="px-1 border-r border-border/30 justify-center"
              >
                <SortableHeader field="start" label="Start" />
              </ResizableColumnHeader>
              <ResizableColumnHeader 
                width={effectiveColumnWidths.end} 
                onResize={(w) => handleColumnResize('end', w)}
                className="px-1 border-r border-border/30 justify-center"
              >
                <SortableHeader field="end" label="End" />
              </ResizableColumnHeader>
              <ResizableColumnHeader 
                width={effectiveColumnWidths.hours} 
                onResize={(w) => handleColumnResize('hours', w)}
                className="px-1 border-r border-border/30 justify-center"
              >
                <SortableHeader field="hours" label="Hrs" />
              </ResizableColumnHeader>
              <ResizableColumnHeader 
                width={effectiveColumnWidths.crew} 
                onResize={(w) => handleColumnResize('crew', w)}
                className="px-1 border-r border-border/30 justify-center"
              >
                <SortableHeader field="crew" label="Crew" />
              </ResizableColumnHeader>
              <ResizableColumnHeader 
                width={effectiveColumnWidths.address} 
                onResize={(w) => handleColumnResize('address', w)}
                className="px-1 justify-center"
              >
                <SortableHeader field="address" label="Address" />
              </ResizableColumnHeader>
            </div>

            {/* Rows */}
            <ScrollArea className="flex-1">
              {loading ? (
                <div className="flex items-center justify-center" style={{ height: `${80 * scale}px` }}>
                  <Loader2 className="animate-spin text-muted-foreground" style={{ width: `${20 * scale}px`, height: `${20 * scale}px` }} />
                </div>
              ) : filteredShifts.length === 0 ? (
                <div className="flex items-center justify-center text-muted-foreground" style={{ height: `${80 * scale}px`, fontSize: `${fontSize}px` }}>
                  No shifts found
                </div>
              ) : (
                <div>
                  {filteredShifts.map((shift) => (
                    <SheetPlannerRow
                      key={shift.id}
                      shift={shift}
                      project={getProject(shift.project_id)}
                      projectName={getProjectName(shift.project_id)}
                      venue={getVenue(shift.venue_id)}
                      venueName={getVenueName(shift.venue_id)}
                      location={getLocation(shift.venue_location_id)}
                      locationName={getLocationName(shift.venue_location_id)}
                      locationAddress={getLocationAddress(shift.venue_location_id)}
                      crew={shiftCrewMap[shift.id] || []}
                      lineItems={shiftLineItemsMap[shift.id] || []}
                      venueLocations={venueLocations.filter((l) => l.venue_id === shift.venue_id)}
                      isExpanded={expandedRows.includes(shift.id)}
                      onToggle={() => handleRowToggle(shift.id)}
                      localEdits={localEdits[shift.id] || {}}
                      onLocalEdit={(field, value) => handleLocalEdit(shift.id, field, value)}
                      onSave={() => handleSaveShift(shift.id)}
                      onCancelEdit={() => handleCancelEdit(shift.id)}
                      onAddCrew={(staffId, role) => handleAddCrew(shift.id, staffId, role)}
                      onRemoveCrew={(staffId) => handleRemoveCrew(shift.id, staffId)}
                      onToggleCrewChief={(staffId, make) => handleToggleCrewChief(shift.id, staffId, make)}
                      onToggleConfirmed={(shiftId, staffId, confirmed) => handleToggleConfirmed(shiftId, staffId, confirmed)}
                      onToggleWorked={(shiftId, staffId, worked) => handleToggleWorked(shiftId, staffId, worked)}
                      onSendPWALink={handleSendPWALink}
                      onCopyPWAInstall={handleCopyPWAInstall}
                      onEmailCrew={handleEmailCrew}
                      onPushCrew={handlePushCrew}
                      onCopyCrew={() => onCopyCrew(shift.id)}
                      onPasteCrew={() => onPasteCrew(shift.id)}
                      hasClipboard={!!crewClipboard}
                      onNavigateProject={() => handleNavigateProject(shift.project_id)}
                      onNavigateShift={() => handleNavigateShift(shift.id)}
                      onNavigateVenue={() => handleNavigateVenue(shift.venue_id)}
                      onNavigateLocation={() => handleNavigateLocation(shift.venue_id, shift.venue_location_id)}
                      onEditCrew={handleEditCrew}
                      columnWidths={effectiveColumnWidths}
                      scale={scale}
                      formatCurrency={formatCurrency}
                    />
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        </div>

        {/* Drag Overlay */}
        <DragOverlay>
          {activeCrew && (
            <div 
              className="flex items-center gap-1 px-2 rounded bg-primary/10 border border-primary/30 shadow-lg"
              style={{ height: `${20 * scale}px`, fontSize: `${10 * scale}px` }}
            >
              <User style={{ width: `${12 * scale}px`, height: `${12 * scale}px` }} />
              <span>
                {activeCrew.first_name} {activeCrew.last_name}
              </span>
            </div>
          )}
        </DragOverlay>
        
        {/* EF-CREW-CONFLICT-PREVENTION-V1: Conflict Override Dialog */}
        <AlertDialog open={conflictDialogOpen} onOpenChange={setConflictDialogOpen}>
          <AlertDialogContent className="max-w-lg">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
                <AlertTriangle className="h-5 w-5" />
                Scheduling Conflict Detected
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    This crew member has overlapping shift assignments:
                  </p>
                  
                  {/* Conflict Details */}
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {pendingConflicts.map((conflict, idx) => (
                      <div 
                        key={idx} 
                        className="p-2 rounded bg-amber-500/10 border border-amber-500/30 text-sm"
                      >
                        <div className="flex items-center gap-2 font-medium text-foreground">
                          <Clock className="h-3.5 w-3.5 text-amber-600" />
                          {conflict.conflicting_shift_name}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {conflict.conflicting_shift_date} • {conflict.other_effective_start?.split(' ')[1]} - {conflict.other_effective_end?.split(' ')[1]}
                        </div>
                        <div className="text-xs text-amber-600 mt-1">
                          Overlap: {conflict.overlap_minutes} minutes
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Override Reason */}
                  <div className="pt-2">
                    <Label className="text-xs font-medium text-muted-foreground">
                      Reason for override (required):
                    </Label>
                    <Textarea
                      value={conflictOverrideReason}
                      onChange={(e) => setConflictOverrideReason(e.target.value)}
                      placeholder="e.g., Crew member confirmed availability, short overlap acceptable..."
                      className="mt-1 h-20 text-sm resize-none"
                      data-testid="conflict-override-reason"
                    />
                  </div>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel 
                onClick={() => {
                  setConflictDialogOpen(false);
                  setPendingConflicts([]);
                  setConflictOverrideReason("");
                  setPendingAssignment(null);
                }}
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConflictOverride}
                disabled={!conflictOverrideReason.trim()}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="conflict-override-confirm"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                Override & Assign
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* EF-PLANNER-DEPRECATION-AND-RENAMING-V1: Onboarding Override Dialog */}
        <AlertDialog open={onboardingOverrideDialogOpen} onOpenChange={setOnboardingOverrideDialogOpen}>
          <AlertDialogContent className="max-w-md">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
                <AlertTriangle className="h-5 w-5" />
                Assign Crew Member with Incomplete Onboarding?
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3">
                  <p className="text-sm text-foreground">
                    <strong>{pendingOnboardingOverride?.staffName || 'This crew member'}</strong> has not completed onboarding (missing documents).
                  </p>
                  
                  <div className="p-3 rounded bg-amber-500/10 border border-amber-500/30">
                    <p className="text-sm text-amber-600">
                      <strong>Reason:</strong> {pendingOnboardingOverride?.reason || 'Missing required documents'}
                    </p>
                  </div>
                  
                  <p className="text-sm text-muted-foreground">
                    Do you want to override this requirement and assign them anyway?
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={handleCloseOnboardingOverrideDialog}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleOnboardingOverride}
                disabled={onboardingOverrideSubmitting}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="onboarding-override-confirm"
              >
                {onboardingOverrideSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Assign Anyway
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* EVENTSFLO-CREW-PLANNER-ASSIGNMENT-OVERRIDE-FOR-INCOMPLETE-ONBOARDING-V1
            Batch override dialog for the Paste Crew flow. Lists every crew
            member the backend rejected with `crew_not_active` + `can_override`
            on the first pass. One confirm-click retries them all with
            `onboarding_override=true`. Cancelling skips those crew. */}
        <AlertDialog
          open={Boolean(pasteOverridePending)}
          onOpenChange={(o) => { if (!o) handleCancelPasteOverride(); }}
        >
          <AlertDialogContent
            className="sm:max-w-lg"
            data-testid="paste-override-dialog"
          >
            <AlertDialogHeader>
              <AlertDialogTitle className="text-amber-600">
                Paste {pasteOverridePending?.overridable?.length || 0} crew with onboarding override?
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3">
                  <p className="text-sm text-foreground">
                    {pasteOverridePending?.added > 0 && (
                      <>
                        <strong>{pasteOverridePending.added}</strong> crew member(s)
                        already added without override.{" "}
                      </>
                    )}
                    The following crew were blocked by the standard onboarding
                    check (missing or expired documents / not active). They
                    will <strong>not</strong> be marked as fully onboarded —
                    each assignment row will carry an explicit
                    &quot;Override&quot; marker.
                  </p>

                  <div className="rounded border border-amber-500/40 bg-amber-50 dark:bg-amber-900/20 p-3 text-xs max-h-56 overflow-y-auto space-y-1">
                    <ul className="list-disc list-inside space-y-1 text-amber-800 dark:text-amber-200">
                      {(pasteOverridePending?.overridable || []).map((c) => (
                        <li key={c.staff_id}>
                          <strong>{c.staff_name}</strong>
                          <span className="text-amber-700 dark:text-amber-300">
                            {" "}— {c.reason}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {(pasteOverridePending?.failedReasons?.length || 0) > 0 && (
                    <div className="rounded border border-red-500/40 bg-red-50 dark:bg-red-900/20 p-3 text-xs">
                      <div className="font-medium text-red-700 dark:text-red-300">
                        Non-overridable failures (will not be assigned):
                      </div>
                      <ul className="list-disc list-inside mt-1 text-red-700 dark:text-red-300">
                        {pasteOverridePending.failedReasons.slice(0, 5).map((f, i) => (
                          <li key={i}>{f}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <p className="text-xs text-muted-foreground">
                    Override events are logged. The crew profile&apos;s
                    onboarding/compliance state is unchanged.
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                onClick={handleCancelPasteOverride}
                data-testid="paste-override-cancel"
              >
                Skip these crew
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmPasteOverride}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="paste-override-confirm"
              >
                Assign all with override
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* EVENTSFLO-CREW-PLANNER-EMAIL-CREW-RESTORE-V1: ad-hoc email modal */}
        <PlannerEmailCrewModal
          open={Boolean(emailCrewContext)}
          onClose={() => setEmailCrewContext(null)}
          shift={emailCrewContext?.shift || null}
          staffId={emailCrewContext?.staffId || null}
          staffName={emailCrewContext?.staffName || ""}
          staffEmail={emailCrewContext?.staffEmail || ""}
          venueLabel={emailCrewContext?.venueLabel || ""}
        />
      </div>
    </DndContext>
  );
}
